import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";
import { Star, MessageSquareQuote, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ReviewSubmit({ children }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [rating, setRating] = useState(5);
  const [content, setContent] = useState("");
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  function reset() { setName(""); setEmail(""); setRating(5); setContent(""); setDone(false); }

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/reviews/submit", { name, email: email || undefined, rating, content });
      setDone(true);
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail) || "Couldn't submit");
    } finally { setSubmitting(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); setOpen(v); }}>
      <DialogTrigger asChild>
        {children || (
          <button data-testid="open-review-form"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium thick-gold bg-[#FDFBF7] hover:bg-[#F5F2EA]">
            <MessageSquareQuote className="w-4 h-4" /> Leave a review
          </button>
        )}
      </DialogTrigger>
      <DialogContent data-testid="review-modal" className="max-w-md bg-[#FDFBF7] rounded-3xl thick-gold">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{done ? "Thank you" : "Leave a review"}</DialogTitle>
          <DialogDescription className="text-[#5C5547]">
            {done ? "We'll review your note and add it to the wall soon."
                  : "Loved something you bought? Tell us. We'll publish it after a quick check."}
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="text-center py-6" data-testid="review-success">
            <CheckCircle2 className="w-10 h-10 mx-auto text-[var(--accent-strong)]" />
            <button onClick={() => { reset(); setOpen(false); }} className="mt-6 px-6 py-2.5 rounded-full bg-[#2C2924] text-[#FDFBF7] text-sm">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="review-form">
            <input required placeholder="Your name (or @handle)" value={name} onChange={(e) => setName(e.target.value)}
              data-testid="review-name" className="w-full px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
            <input type="email" placeholder="Email (optional, won't be published)" value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="review-email" className="w-full px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
            <div className="flex items-center gap-2" data-testid="review-stars">
              <span className="text-xs text-[#5C5547] uppercase tracking-[0.2em]">Rating</span>
              {[1, 2, 3, 4, 5].map((n) => (
                <button type="button" key={n} onClick={() => setRating(n)}
                  className={n <= rating ? "text-[var(--accent-strong)]" : "text-[#EAE4D3]"}>
                  <Star className="w-5 h-5 fill-current" />
                </button>
              ))}
            </div>
            <textarea required rows={4} placeholder="What did you love? (10+ characters)"
              value={content} onChange={(e) => setContent(e.target.value)}
              data-testid="review-content" className="w-full px-4 py-3 rounded-xl bg-[#F5F2EA] border border-[#EAE4D3] text-sm" />
            <button type="submit" disabled={submitting} data-testid="review-submit"
              className="w-full py-3 rounded-full bg-[#2C2924] text-[#FDFBF7] text-sm disabled:opacity-60">
              {submitting ? "Sending…" : "Submit review"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
